'use client'

import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  ScatterChart,
  Scatter,
  ReferenceDot
} from 'recharts'
import { Skeleton } from '@dd3tech/wiggui/Skeleton'
import type { IconType } from 'react-icons'

import { composeClasses } from '@wiggot/utils'

import { CustomReference } from './components/custom-reference'

export * from './components'

const COLORS = ['#0368FE', '#0368FE32']

interface CapitalGainChartProps<T> {
  height: string
  data?: T[][]
  title?: string
  icon?: IconType
  unit?: string
  labels?: [string, string]
  dataKeys?: [string, string]
  referencePoint?: [number, number]
  className?: string
  isLoading?: boolean
}

export const ComparablesChart = <T,>({
  height,
  data,
  title,
  icon,
  unit,
  labels,
  dataKeys = ['x', 'y'],
  referencePoint,
  className,
  isLoading
}: CapitalGainChartProps<T>) => {
  if (isLoading) {
    return <Skeleton width="w-full" height={height} />
  }

  return (
    <div
      className={composeClasses(
        'flex flex-col gap-2 w-full border rounded-xl p-4',
        height,
        className
      )}
    >
      {title && (
        <div className="flex items-center gap-2">
          {icon && (
            <div className="p-1 rounded-md bg-surface-gray-10">{icon({})}</div>
          )}
          <span className="text-body-m">{title}</span>
        </div>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart
          margin={{
            top: 10,
            right: labels ? 15 : 0,
            bottom: labels ? 15 : -10,
            left: labels ? 5 : -15
          }}
        >
          <CartesianGrid
            stroke="#EBEDF0"
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey={dataKeys[0]}
            type="number"
            domain={['auto', 'auto']}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#121212' }}
            label={
              labels && {
                value: labels[0],
                position: 'insideBottom',
                dy: 15,
                style: { fontSize: 12, fontWeight: 500 },
                fill: '#121212'
              }
            }
            padding={{ left: 15, right: 15 }}
            unit={unit}
          />
          <YAxis
            dataKey={dataKeys[1]}
            type="number"
            domain={['auto', 'auto']}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => (value / 1000).toString()}
            tick={{ fontSize: 12, fill: '#121212' }}
            label={
              labels && {
                value: labels[1],
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fontSize: 12, fontWeight: 500 },
                fill: '#121212'
              }
            }
            unit="K"
          />
          {data?.map((item, index) => (
            <Scatter
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              data={item}
              fill={COLORS[index % COLORS.length]}
            />
          ))}
          {referencePoint && (
            <ReferenceDot
              x={referencePoint[0]}
              y={referencePoint[1]}
              shape={CustomReference}
              ifOverflow="extendDomain"
            />
          )}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  )
}
